/**
 * OPM Editor X6 节点尺寸工具
 * 根据内容和子节点自动计算尺寸
 */

export interface NodeSize {
  width: number;
  height: number;
}

// 每种节点类型的最小尺寸
export const MIN_SIZES: Record<string, NodeSize> = {
  object: { width: 180, height: 80 },
  option: { width: 180, height: 80 },
  process: { width: 100, height: 80 },
  state: { width: 80, height: 60 },
  value: { width: 70, height: 30 },
};

/**
 * Object 节点内部布局常量。
 *
 * 布局（从卡片顶部起）：
 *   [标题栏]               HEADER_HEIGHT (22px)
 *   [内容区上边距]         BODY_PAD_TOP (16px)  ← 标签从此开始
 *   [标签]                 LABEL_H (~18px)
 *   [间距]                 LABEL_STATE_GAP (10px)
 *   [状态行]               可变
 *   [内容区下边距]         BODY_PAD_BOTTOM (16px)
 */
export const OBJ_LAYOUT = {
  HEADER_HEIGHT: 22,
  BODY_PAD_TOP: 16,     // 标题栏底部到标签顶部的距离
  LABEL_H: 18,          // 标签行高度（近似值）
  LABEL_STATE_GAP: 10,  // 标签底部到第一行状态的间距
  STATE_GAP_X: 24,      // 状态之间的水平间距
  STATE_GAP_Y: 24,      // 状态行之间的垂直间距
  BODY_PAD_BOTTOM: 16,  // 状态下方内边距
  PAD_X: 24,            // 内容区水平内边距
  STATES_PER_ROW: 3,
};

/**
 * 根据标签计算状态节点的自然宽度。
 */
export function calculateNaturalStateWidths(labels: string[]): number[] {
  return labels.map(label => calculateStateSize(label).width);
}

/**
 * 计算容纳自然宽度状态所需的最小父节点宽度。
 */
export function calculateMinParentWidthForNaturalStates(stateWidths: number[]): number {
  const { STATE_GAP_X, PAD_X, STATES_PER_ROW } = OBJ_LAYOUT;
  let maxRowWidth = 0;
  for (let row = 0; row * STATES_PER_ROW < stateWidths.length; row++) {
    const start = row * STATES_PER_ROW;
    const end = Math.min(start + STATES_PER_ROW, stateWidths.length);
    const widthsInRow = stateWidths.slice(start, end);
    const rowWidth = widthsInRow.reduce((sum, w) => sum + w, 0) + (widthsInRow.length - 1) * STATE_GAP_X;
    maxRowWidth = Math.max(maxRowWidth, rowWidth);
  }
  return maxRowWidth + PAD_X * 2;
}

/**
 * 计算状态节点在父 Object 内的绝对位置。
 * 每个状态使用其自然宽度；各行在父节点内居中排列。
 */
export function calculateStatePositions(
  parentPos: { x: number; y: number },
  parentWidth: number,
  stateCount: number,
  stateWidths?: number[],
  stateHeight: number = MIN_SIZES.state.height,
): Array<{ x: number; y: number; width: number }> {
  const { HEADER_HEIGHT, BODY_PAD_TOP, LABEL_H, LABEL_STATE_GAP, STATE_GAP_X, STATE_GAP_Y, STATES_PER_ROW } = OBJ_LAYOUT;

  if (stateCount === 0) return [];

  // 未提供宽度时回退为等宽分布
  const widths = stateWidths && stateWidths.length === stateCount
    ? stateWidths
    : Array(stateCount).fill(MIN_SIZES.state.width);

  return Array.from({ length: stateCount }, (_, i) => {
    const row = Math.floor(i / STATES_PER_ROW);
    const col = i % STATES_PER_ROW;
    const rowStart = row * STATES_PER_ROW;
    const colsInThisRow = Math.min(stateCount - rowStart, STATES_PER_ROW);
    const widthsInRow = widths.slice(rowStart, rowStart + colsInThisRow);
    const rowTotalWidth = widthsInRow.reduce((sum, w) => sum + w, 0) + (colsInThisRow - 1) * STATE_GAP_X;
    const rowOffsetX = (parentWidth - rowTotalWidth) / 2;

    let xOffset = 0;
    for (let c = 0; c < col; c++) {
      xOffset += widths[rowStart + c] + STATE_GAP_X;
    }

    return {
      x: parentPos.x + rowOffsetX + xOffset,
      y: parentPos.y + HEADER_HEIGHT + BODY_PAD_TOP + LABEL_H + LABEL_STATE_GAP + row * (stateHeight + STATE_GAP_Y),
      width: widths[i],
    };
  });
}

/**
 * 计算每行容纳 N 个最小尺寸状态所需的最小父节点宽度。
 */
export function calculateMinParentWidthForStates(
  stateCount: number,
  stateMinWidth: number = MIN_SIZES.state.width,
): number {
  const { STATE_GAP_X, PAD_X, STATES_PER_ROW } = OBJ_LAYOUT;
  const cols = Math.min(stateCount, STATES_PER_ROW);
  return PAD_X * 2 + cols * stateMinWidth + (cols - 1) * STATE_GAP_X;
}

/**
 * 使用 Canvas API 测量文本宽度
 * 若 Canvas 不可用则回退为估算值
 */
let canvas: HTMLCanvasElement | null = null;
function measureText(text: string, fontSize: number, fontWeight: number = 600): number {
  if (!canvas) {
    canvas = document.createElement('canvas');
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // 回退方案：在字号 12 时按每字符 7px 估算
    return text.length * (fontSize / 12) * 7;
  }
  ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  return ctx.measureText(text).width;
}

/**
 * 根据标签计算 State 节点的尺寸
 */
export function calculateStateSize(label: string): NodeSize {
  const textWidth = measureText(label, 11, 600);
  const minWidth = MIN_SIZES.state.width;
  const minHeight = MIN_SIZES.state.height;

  // 内边距：水平 16px（每侧 8px），垂直 6px
  const width = Math.max(minWidth, textWidth + 32);
  const height = minHeight;

  return { width, height };
}

/**
 * 根据标签计算 Value 节点的尺寸
 */
export function calculateValueSize(label: string): NodeSize {
  const textWidth = measureText(label, 11, 600);
  const minWidth = MIN_SIZES.value.width;
  const minHeight = MIN_SIZES.value.height;

  // 内边距：水平 16px（每侧 8px），垂直 6px
  const width = Math.max(minWidth, textWidth + 32);
  const height = minHeight;

  return { width, height };
}

/**
 * 根据标签和子节点计算 Object 节点的尺寸。
 * @param hasStates - 若为 true，则在标签下方预留状态节点的空间
 */
export function calculateObjectSize(
  label: string,
  hasStates: boolean,
  children?: Array<{ x: number; y: number; width: number; height: number }>
): NodeSize {
  const minWidth = MIN_SIZES.object.width;
  const minHeight = MIN_SIZES.object.height;
  const { HEADER_HEIGHT, BODY_PAD_TOP, LABEL_H, LABEL_STATE_GAP, BODY_PAD_BOTTOM, PAD_X } = OBJ_LAYOUT;

  // 内容区需要容纳标签（以及可能存在的状态）
  const labelWidth = measureText(label, 13, 600);
  let bodyWidth = labelWidth + PAD_X * 2;

  // 标签始终从标题栏底部偏移 BODY_PAD_TOP 处开始
  // 若有状态，则加上间距和状态高度；否则仅保留内边距
  let bodyHeight = BODY_PAD_TOP + LABEL_H + BODY_PAD_BOTTOM;
  if (hasStates) {
    bodyHeight = BODY_PAD_TOP + LABEL_H + LABEL_STATE_GAP + MIN_SIZES.state.height + BODY_PAD_BOTTOM;
  }

  if (children && children.length > 0) {
    // 计算子节点的包围盒（相对于父节点）
    let maxX = 0;
    let maxY = 0;
    children.forEach(child => {
      const childRight = child.x + child.width;
      const childBottom = child.y + child.height;
      if (childRight > maxX) maxX = childRight;
      if (childBottom > maxY) maxY = childBottom;
    });

    // 内容区需要包含所有子节点并留有内边距
    bodyWidth = Math.max(bodyWidth, maxX + PAD_X);
    bodyHeight = Math.max(bodyHeight, maxY - HEADER_HEIGHT + BODY_PAD_BOTTOM);
  }

  const width = Math.max(minWidth, bodyWidth);
  const height = Math.max(minHeight, HEADER_HEIGHT + bodyHeight);

  return { width, height };
}

/**
 * 根据标签计算 Process 节点（圆形）的尺寸
 */
export function calculateProcessSize(label: string): NodeSize {
  const minWidth = MIN_SIZES.process.width;
  const minHeight = MIN_SIZES.process.height;

  // Process 是圆形，因此取较大维度
  const textWidth = measureText(label, 12, 600);

  // 需要足够的空间容纳文本和内边距（圆形）
  // 圆的直径至少为文本宽度 + 32px 内边距
  const diameter = Math.max(minWidth, textWidth + 48);

  return { width: diameter, height: diameter };
}
